import { atom, getDefaultStore, useAtomValue, type Atom } from 'jotai'
import { TranscriptEntry, User } from '@highlight/common'
import { transcriptMergingAtom } from './use-audio-transcript-merging'
import { transcriptViewAtom } from '@renderer/overlay/Sidepanel/components/meeting/atoms'
import { mapTranscriptEntriesToMessages } from '@renderer/utils/audio-note-helpers'
import { atomWithGlobalState } from '@highlight/ipc/jotai'
import type { AudioNoteState } from '@shared/types/AudioNoteState'
import type { TranscriptBufferKey } from '@shared/transcript-feed'
import { USER_STATE } from '@shared/ipc/GlobalStateUtils'
import { getUserFullName } from '@renderer/utils/user-name'
import { USER_VOICE_MATCHES_STATE } from '@shared/self-voice'
import type { UserVoiceMatches } from '@shared/utils/audio-notes/mic-label'
import { acquireTranscriptFeed, fetchTranscriptBuffers, peekTranscriptBuffers } from './transcript-feed'

const userAtom = atomWithGlobalState<User | undefined>(USER_STATE, undefined)

export const audioNoteStateAtom = atomWithGlobalState<AudioNoteState | undefined>('audio-note-state', undefined)

/**
 * Live transcript buffers, delivered via the on-demand transcript feed
 * (snapshot + deltas) instead of GlobalState. The feed subscription only
 * exists while one of these atoms is mounted — an app with no transcript
 * view open receives zero transcript IPC.
 */
function atomWithTranscriptBuffer(key: TranscriptBufferKey): Atom<Array<TranscriptEntry>> {
  const bufferAtom = atom<Array<TranscriptEntry>>(peekTranscriptBuffers()[key])
  bufferAtom.onMount = (set) => {
    const release = acquireTranscriptFeed(() => set(peekTranscriptBuffers()[key]))
    set(peekTranscriptBuffers()[key])
    return release
  }
  return bufferAtom
}

export const processedEntriesAtom = atomWithTranscriptBuffer('processed')
export const providerEntriesAtom = atomWithTranscriptBuffer('provider')
export const mergedEntriesAtom = atomWithTranscriptBuffer('merged')

export type TranscriptSource = 'provider' | 'saved' | 'merged' | 'processed'

/**
 * Single source of truth for which transcript buffer is "active": raw view
 * prefers provider entries so Deepgram's default speaker labels surface
 * unmodified; labeled view prefers saved-note entries, then merged (while
 * merging), then processed, with provider as the last resort. Shared by
 * `activeTranscriptEntriesAtom` (full entries) and `useHasTranscript`
 * (presence booleans) so the two selection orders can't drift.
 */
export function selectTranscriptSource(args: {
  view: 'labeled' | 'raw'
  isMerging: boolean
  hasProvider: boolean
  hasSaved: boolean
  hasMerged: boolean
  hasProcessed: boolean
}): TranscriptSource {
  if (args.view === 'raw' && args.hasProvider) return 'provider'
  if (args.hasSaved) return 'saved'
  if (args.view === 'labeled' && args.isMerging && args.hasProvider && args.hasMerged) return 'merged'
  return args.hasProcessed ? 'processed' : 'provider'
}

/** This will always return the transcript entries that should be displayed to the user. */
export const activeTranscriptEntriesAtom = atom((get) => {
  const provider = get(providerEntriesAtom)
  // Entries are stripped from GlobalState after save; prefer feed buffers.
  // Legacy dual-read: older payloads may still embed transcriptEntries.
  const audioNoteState = get(audioNoteStateAtom)
  const savedEntries = audioNoteState?.savedNote?.transcriptEntries
  const hasSaved = !!audioNoteState?.savedNoteHasTranscript || !!savedEntries?.length
  const merged = get(mergedEntriesAtom)
  const processed = get(processedEntriesAtom)

  const source = selectTranscriptSource({
    view: get(transcriptViewAtom),
    isMerging: get(transcriptMergingAtom),
    hasProvider: provider.length > 0,
    hasSaved,
    hasMerged: merged.length > 0,
    hasProcessed: processed.length > 0,
  })

  switch (source) {
    case 'provider':
      return provider
    case 'saved':
      // Post-save UI should load entries via by-id / Meeting View; if a legacy
      // payload still has them, use those. Otherwise fall through to processed.
      return savedEntries?.length ? savedEntries : processed
    case 'merged':
      return merged
    case 'processed':
      return processed
  }
})

const resolvedNamesAtom = atomWithGlobalState<Record<string, string>>('speaker-resolved-names', {})
const manualOverridesAtom = atomWithGlobalState<Record<string, string>>('speaker-manual-overrides', {})
const userVoicesAtom = atomWithGlobalState<UserVoiceMatches>(USER_VOICE_MATCHES_STATE, {})

/**
 * Map transcript entries to messages for display in the UI.
 * In labeled view, manual overrides are merged on top of auto-resolved names.
 * In raw view both override maps are dropped so the underlying Deepgram
 * speaker labels show — mic entries still resolve against the current owner
 * name via resolveMicLabel ("{Owner}'s mic — A") so the mic/system
 * distinction is preserved; only the name overrides are skipped.
 */
function buildTranscriptMessages(
  entries: Array<TranscriptEntry>,
  view: 'labeled' | 'raw',
  user: User | undefined,
  resolvedNames: Record<string, string>,
  manualOverrides: Record<string, string>,
  userVoices: UserVoiceMatches,
): ReturnType<typeof mapTranscriptEntriesToMessages> {
  const ownerName = getUserFullName(user)
  if (view === 'raw') {
    return mapTranscriptEntriesToMessages(entries, undefined, ownerName)
  }
  const hasOverrides = Object.keys(manualOverrides).length > 0
  const hasResolved = Object.keys(resolvedNames).length > 0
  // Merge manual overrides on top (they use full prefixed keys like "mic:2", "sys:0")
  const effectiveNames = hasResolved || hasOverrides ? { ...resolvedNames, ...manualOverrides } : undefined
  return mapTranscriptEntriesToMessages(entries, effectiveNames, ownerName, userVoices)
}

const activeTranscriptMessagesAtom = atom((get) => {
  return buildTranscriptMessages(
    get(activeTranscriptEntriesAtom),
    get(transcriptViewAtom),
    get(userAtom),
    get(resolvedNamesAtom),
    get(manualOverridesAtom),
    get(userVoicesAtom),
  )
})

/**
 * Imperative version of useAudioTranscript. Async because the transcript
 * buffers live in the main process: if no transcript view is mounted this
 * fetches a one-shot snapshot instead of relying on a live feed.
 */
export const getActiveTranscript = async (): Promise<{
  entries: Array<TranscriptEntry>
  messages: ReturnType<typeof mapTranscriptEntriesToMessages>
}> => {
  const store = getDefaultStore()
  const buffers = await fetchTranscriptBuffers()
  const audioNoteState = store.get(audioNoteStateAtom)
  const savedEntries = audioNoteState?.savedNote?.transcriptEntries
  const hasSaved = !!audioNoteState?.savedNoteHasTranscript || !!savedEntries?.length
  const view = store.get(transcriptViewAtom)

  const source = selectTranscriptSource({
    view,
    isMerging: store.get(transcriptMergingAtom),
    hasProvider: buffers.provider.length > 0,
    hasSaved,
    hasMerged: buffers.merged.length > 0,
    hasProcessed: buffers.processed.length > 0,
  })

  const entries = source === 'saved' ? (savedEntries?.length ? savedEntries : buffers.processed) : buffers[source]
  const messages = buildTranscriptMessages(
    entries,
    view,
    store.get(userAtom),
    store.get(resolvedNamesAtom),
    store.get(manualOverridesAtom),
    store.get(userVoicesAtom),
  )
  return { entries, messages }
}

export const useAudioTranscript = () => {
  const entries = useAtomValue(activeTranscriptEntriesAtom)
  const messages = useAtomValue(activeTranscriptMessagesAtom)
  return { entries, messages }
}
